import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type {
  AdminIapStatusFilter,
  AdminIapTransaction,
  AdminIapTransactionsResponse,
} from '@shared/types';
import { DataTable, type DataTableColumn } from '../../components/DataTable';
import PageHeader from '../../components/PageHeader';
import Pagination from '../../components/Pagination';
import Pill from '../../components/Pill';
import {
  listIapTransactions,
  type ListIapTransactionsParams,
} from '../../services/liveActivity';
import { ApiError } from '../../services/api';

/**
 * In-app purchases — the drill-down behind the dashboard's revenue /
 * transactions / refunded cards.
 *
 * The headline figure counts PRODUCTION rows only, matching the dashboard
 * card exactly: `priceUsd` is the catalog list price, so a sandbox
 * (TestFlight / license-tester) row is not money (#585). Sandbox rows are
 * still listed — hiding them would leave an operator unable to explain the
 * gap between "5 transactions" and the revenue total.
 *
 * Filters live in the URL so the dashboard can deep-link
 * (`/purchases?status=refunded`) and a filtered view survives a refresh.
 */

const PAGE_SIZE = 25;

// Aliased to the shared union rather than re-declared: the local copy omitted
// `'live'`, so `?status=live` from the dashboard failed `isOneOf` and loaded
// the page UNFILTERED — the card said 5 transactions and the list it opened
// said 8.
type StatusFilter = AdminIapStatusFilter;
type EnvFilter = 'production' | 'sandbox' | 'unknown';
type PlatformFilter = 'apple' | 'google';

const STATUSES: StatusFilter[] = [
  'live',
  'granted',
  'verified',
  'refunded',
  'revoked',
];

/** `live` needs spelling out — it is a set, not a stored status value. */
const STATUS_LABELS: Record<string, string> = {
  live: 'live (granted + verified)',
};
const ENVIRONMENTS: EnvFilter[] = ['production', 'sandbox', 'unknown'];
const PLATFORMS: PlatformFilter[] = ['apple', 'google'];

function isOneOf<T extends string>(list: T[], v: string | null): v is T {
  return v !== null && (list as string[]).includes(v);
}

export default function PurchasesList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const statusParam = searchParams.get('status');
  const envParam = searchParams.get('environment');
  const platformParam = searchParams.get('platform');

  const status = isOneOf(STATUSES, statusParam) ? statusParam : undefined;
  const environment = isOneOf(ENVIRONMENTS, envParam) ? envParam : undefined;
  const platform = isOneOf(PLATFORMS, platformParam) ? platformParam : undefined;

  const [page, setPage] = useState(1);
  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'ready'; data: AdminIapTransactionsResponse }
    | { status: 'error'; message: string }
  >({ status: 'loading' });

  const setFilter = useCallback(
    (key: string, value: string) => {
      const next = new URLSearchParams(searchParams);
      if (value === '') next.delete(key);
      else next.set(key, value);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // A filter change can shrink the result set below the current page.
  useEffect(() => {
    setPage(1);
  }, [status, environment, platform]);

  const reqCounterRef = useRef(0);
  const fetchRows = useCallback(async () => {
    const reqId = ++reqCounterRef.current;
    setState({ status: 'loading' });
    try {
      const params: ListIapTransactionsParams = {
        page,
        limit: PAGE_SIZE,
        status,
        environment,
        platform,
      };
      const data = await listIapTransactions(params);
      if (reqId !== reqCounterRef.current) return;
      setState({ status: 'ready', data });
    } catch (err) {
      if (reqId !== reqCounterRef.current) return;
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to load purchases';
      setState({ status: 'error', message });
    }
  }, [page, status, environment, platform]);

  useEffect(() => {
    void fetchRows();
  }, [fetchRows]);

  const rows = state.status === 'ready' ? state.data.data : [];
  const totalPages = useMemo(() => {
    if (state.status !== 'ready' || state.data.total === 0) return 1;
    return Math.max(1, Math.ceil(state.data.total / state.data.limit));
  }, [state]);

  const columns: DataTableColumn<AdminIapTransaction>[] = useMemo(
    () => [
      {
        key: 'productId',
        header: 'Product',
        render: (t) => (
          <span className="font-medium text-slate-900">{t.productId}</span>
        ),
      },
      {
        key: 'priceUsd',
        header: 'List price',
        render: (t) => (
          <span className="font-mono text-sm text-slate-700">
            ${t.priceUsd.toFixed(2)}
          </span>
        ),
      },
      {
        key: 'environment',
        header: 'Environment',
        render: (t) => (
          <Pill
            variant={
              t.environment === 'production'
                ? 'success'
                : t.environment === 'sandbox'
                  ? 'warning'
                  : 'neutral'
            }
            testId={`purchase-env-${t.environment}`}
          >
            {t.environment}
          </Pill>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        render: (t) => (
          <Pill
            variant={
              t.status === 'refunded' || t.status === 'revoked'
                ? 'danger'
                : 'neutral'
            }
          >
            {t.status}
          </Pill>
        ),
      },
      {
        key: 'platform',
        header: 'Platform',
        render: (t) => (
          <span className="text-xs uppercase tracking-wide text-slate-700">
            {t.platform}
          </span>
        ),
      },
      {
        key: 'username',
        header: 'Buyer',
        render: (t) => (
          <span className="text-slate-700">
            {t.username ?? (
              <span className="italic text-slate-400">deleted account</span>
            )}
          </span>
        ),
      },
      {
        key: 'createdAt',
        header: 'Date',
        render: (t) => (
          <span className="text-xs text-slate-500">
            {formatDate(t.createdAt)}
          </span>
        ),
      },
    ],
    [],
  );

  const revenue =
    state.status === 'ready' ? state.data.productionRevenueUsd : 0;
  const total = state.status === 'ready' ? state.data.total : 0;

  return (
    <div className="space-y-4" data-testid="purchases-screen">
      <PageHeader
        title="In-app purchases"
        subtitle={
          <>
            <span data-testid="purchases-revenue">
              ${revenue.toFixed(2)} production revenue
            </span>
            {' · '}
            {total} matching {total === 1 ? 'transaction' : 'transactions'}
            {' · '}
            <span className="text-slate-500">
              gross list price, sandbox excluded
            </span>
          </>
        }
        testId="purchases-header"
      />

      <div className="bg-white rounded border border-slate-200 p-3 flex flex-wrap items-end gap-3">
        <FilterSelect
          label="Environment"
          value={environment ?? ''}
          options={ENVIRONMENTS}
          onChange={(v) => setFilter('environment', v)}
          testId="purchases-filter-environment"
        />
        <FilterSelect
          label="Status"
          value={status ?? ''}
          options={STATUSES}
          labels={STATUS_LABELS}
          onChange={(v) => setFilter('status', v)}
          testId="purchases-filter-status"
        />
        <FilterSelect
          label="Platform"
          value={platform ?? ''}
          options={PLATFORMS}
          onChange={(v) => setFilter('platform', v)}
          testId="purchases-filter-platform"
        />
      </div>

      {state.status === 'error' && (
        <div
          role="alert"
          data-testid="purchases-error"
          className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2"
        >
          {state.message}
        </div>
      )}

      <DataTable<AdminIapTransaction>
        columns={columns}
        rows={rows}
        rowKey={(t) => t.id}
        loading={state.status === 'loading'}
        onRowClick={(t) => t.userId && navigate(`/users/${t.userId}`)}
        empty="No purchases match these filters."
        testId="purchases-table"
      />

      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        disabled={state.status === 'loading'}
      />
    </div>
  );
}

function FilterSelect({
  label,
  value,
  options,
  labels,
  onChange,
  testId,
}: {
  label: string;
  value: string;
  options: string[];
  /** Optional display overrides for values whose raw form is unclear. */
  labels?: Record<string, string>;
  onChange: (value: string) => void;
  testId: string;
}) {
  return (
    <label className="flex flex-col text-xs text-slate-600">
      {label}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="mt-1 rounded border border-slate-300 px-2 py-1 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400"
      >
        <option value="">All</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {labels?.[o] ?? o}
          </option>
        ))}
      </select>
    </label>
  );
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch {
    return iso;
  }
}
